import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import api, { API_ENDPOINTS } from '../config/api';
import { 
  Edit, 
  Save, 
  User, 
  Mail, 
  MapPin, 
  Calendar,
  Star,
  Award,
  Users,
  Share2,
  ExternalLink,
  Camera,
  Upload,
  Download,
  Linkedin,
  Github,
  Globe,
  Briefcase,
  GraduationCap,
  Languages,
  X,
  Plus,
  CheckCircle,
  BriefcaseIcon,
  DollarSign,
  Clock,
  MessageCircle,
  Trash2
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const EMPTY_RATING_DISTRIBUTION = {
  5: 0,
  4: 0,
  3: 0,
  2: 0,
  1: 0
};

const ProfilePage = () => {
  const { user: currentUser, updateUser } = useAuth();
  const { username } = useParams();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [editingField, setEditingField] = useState(null);
  const [editingExperience, setEditingExperience] = useState(null);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [newSkill, setNewSkill] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  const isOwnProfile = Boolean(!username || (currentUser && username === currentUser.username));
  const viewingSomeoneElse = Boolean(username && !isOwnProfile);
  const shouldLoadPrivateData = Boolean(isOwnProfile && currentUser);

  const {
    data: publicProfile,
    isLoading: publicProfileLoading,
    error: publicProfileError
  } = useQuery(
    ['publicProfile', username],
    () => api.get(API_ENDPOINTS.USERS.PROFILE(username)),
    {
      enabled: viewingSomeoneElse,
      select: (data) => data.data
    }
  );

  const publicProfileErrorMessage = publicProfileError?.response?.data?.error;

  const user = isOwnProfile ? currentUser : publicProfile;

  // Fetch user stats (only for own profile)
  const { data: privateStats, isLoading: statsLoading } = useQuery(
    ['userStats', currentUser?._id],
    () => api.get(API_ENDPOINTS.USERS.STATS),
    {
      enabled: shouldLoadPrivateData,
      select: (data) => data.data,
    }
  );

  // Fetch ratings (only for own profile)
  const { data: privateRatingsData } = useQuery(
    ['userRatings', currentUser?._id],
    () => api.get(API_ENDPOINTS.USERS.RATINGS),
    {
      enabled: shouldLoadPrivateData,
      select: (data) => data.data,
    }
  );

  // Fetch activity (only for own profile)
  const { data: activityData, isLoading: activityLoading } = useQuery(
    ['userActivity', currentUser?._id],
    () => api.get(API_ENDPOINTS.USERS.ACTIVITY),
    {
      enabled: shouldLoadPrivateData,
      select: (data) => data.data,
    }
  );

  // Fetch public ratings when viewing another user's profile
  const { data: publicRatingsData, isLoading: publicRatingsLoading } = useQuery(
    ['publicRatings', username],
    () => api.get(API_ENDPOINTS.USERS.PROFILE_RATINGS(username)),
    {
      enabled: viewingSomeoneElse,
      select: (data) => data.data,
    }
  );

  const { data: ownerPublicRatingsData, isLoading: ownerPublicRatingsLoading } = useQuery(
    ['ownerPublicRatings', currentUser?.username],
    () => api.get(API_ENDPOINTS.USERS.PROFILE_RATINGS(currentUser.username)),
    {
      enabled: isOwnProfile && !!currentUser?.username,
      select: (data) => data.data,
    }
  );

  const submitRatingMutation = useMutation(
    (payload) => {
      if (!user?._id) {
        throw new Error('User profile not loaded');
      }
      return api.post(API_ENDPOINTS.USERS.RATE_USER(user._id), payload);
    },
    {
      onSuccess: (response) => {
        toast.success(response?.data?.message || 'Thanks for sharing your feedback!');
        if (viewingSomeoneElse) {
          queryClient.invalidateQueries(['publicRatings', username]);
          queryClient.invalidateQueries(['publicProfile', username]);
        } else {
          queryClient.invalidateQueries(['userRatings', currentUser?._id]);
          queryClient.invalidateQueries(['userStats', currentUser?._id]);
        }
        queryClient.invalidateQueries('jobs');
        queryClient.invalidateQueries('gigs');
        queryClient.invalidateQueries('userSearch');
      },
      onError: (error) => {
        const serverMessage = error.response?.data?.error;
        const serverDetails = error.response?.data?.details;
        toast.error(
          serverDetails
            ? `${serverMessage || 'Unable to submit rating'}: ${serverDetails}`
            : serverMessage || 'Unable to submit rating'
        );
      },
    }
  );

  const handleSubmitRating = ({ rating, comment }) => {
    if (!viewingSomeoneElse) {
      toast.error('Ratings can only be left on other profiles.');
      return;
    }

    if (!currentUser) {
      toast.error('Please log in to leave a rating.');
      return;
    }

    submitRatingMutation.mutate({
      rating,
      comment: comment?.trim() ? comment.trim() : undefined,
    });
  };

  // Update profile mutation
  const updateProfileMutation = useMutation(
    (data) => api.put(API_ENDPOINTS.USERS.UPDATE_PROFILE, data),
    {
      onSuccess: (response) => {
        updateUser(response.data);
        queryClient.invalidateQueries(['userStats', currentUser?._id]);
        toast.success('Profile updated successfully');
        setEditingField(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update profile');
      },
    }
  );

  useEffect(() => {
    if (!isOwnProfile || !privateStats?.stats || !currentUser) {
      return;
    }

    const incomingRatingAverage = privateStats?.rating?.average ?? privateStats?.stats?.rating?.average;
    const existingRatingAverage = currentUser?.stats?.rating?.average;

    const ratingsMatch =
      incomingRatingAverage === undefined ||
      (existingRatingAverage !== undefined &&
        Number(existingRatingAverage).toFixed(2) === Number(incomingRatingAverage).toFixed(2));

    const countsMatch =
      privateStats?.rating?.count === undefined ||
      privateStats?.rating?.count === currentUser?.stats?.rating?.count;

    if (ratingsMatch && countsMatch) {
      return;
    }

    const mergedStats = {
      ...(currentUser.stats || {}),
      ...privateStats.stats,
      rating: privateStats.rating || privateStats.stats?.rating || currentUser.stats?.rating
    };

    // Only update if stats actually changed to prevent infinite loops
    const statsChanged = JSON.stringify(mergedStats) !== JSON.stringify(currentUser.stats);
    if (statsChanged) {
      updateUser({
        ...currentUser,
        stats: mergedStats
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnProfile, privateStats?.stats, privateStats?.rating]);

  if (viewingSomeoneElse) {
    if (publicProfileLoading || !user) {
      if (publicProfileLoading) {
        return (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
          </div>
        );
      }

      return (
        <div className="p-6">
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🤔</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Profile not found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {publicProfileErrorMessage || 'The profile you are looking for does not exist or is unavailable.'}
            </p>
          </div>
        </div>
      );
    }
  } else if (!user) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Please log in to view your profile
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            You need to be authenticated to access this page.
          </p>
        </div>
      </div>
    );
  }

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleSaveProfile = (field, value) => {
    const updateData = {};
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updateData[parent] = { ...user[parent], [child]: value };
    } else {
      updateData[field] = value;
    }
    updateProfileMutation.mutate(updateData);
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !user.profile?.skills?.includes(newSkill.trim())) {
      const updatedSkills = [...(user.profile?.skills || []), newSkill.trim()];
      handleSaveProfile('profile', { ...user.profile, skills: updatedSkills });
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    const updatedSkills = (user.profile?.skills || []).filter(s => s !== skillToRemove);
    handleSaveProfile('profile', { ...user.profile, skills: updatedSkills });
  };

  const handleAddLanguage = () => {
    if (newLanguage.trim() && !user.profile?.languages?.includes(newLanguage.trim())) {
      const updatedLanguages = [...(user.profile?.languages || []), newLanguage.trim()];
      handleSaveProfile('profile', { ...user.profile, languages: updatedLanguages });
      setNewLanguage('');
    }
  };

  const handleRemoveLanguage = (languageToRemove) => {
    const updatedLanguages = (user.profile?.languages || []).filter(l => l !== languageToRemove);
    handleSaveProfile('profile', { ...user.profile, languages: updatedLanguages });
  };

  const handleAddExperience = (experience) => {
    const updatedExperience = [...(user.profile?.experience || []), experience];
    handleSaveProfile('profile', { ...user.profile, experience: updatedExperience });
    setEditingExperience(null);
  };

  const handleUpdateExperience = (index, experience) => {
    const updatedExperience = [...(user.profile?.experience || [])];
    updatedExperience[index] = experience;
    handleSaveProfile('profile', { ...user.profile, experience: updatedExperience });
    setEditingExperience(null);
  };

  const handleRemoveExperience = (index) => {
    const updatedExperience = (user.profile?.experience || []).filter((_, i) => i !== index);
    handleSaveProfile('profile', { ...user.profile, experience: updatedExperience });
  };

  const handleAddPortfolio = async (portfolio) => {
    // If portfolio has imageFile, we need to upload it
    if (portfolio.imageFile) {
      const formData = new FormData();
      const currentPortfolio = user.profile?.portfolio || [];
      const newIndex = currentPortfolio.length;
      const portfolioData = [
        ...currentPortfolio.map(item => ({
          title: item.title,
          description: item.description,
          url: item.url,
          image: item.image || item.imageUrl || ''
        })),
        {
          title: portfolio.title,
          description: portfolio.description,
          url: portfolio.url,
          imageIndex: newIndex
        }
      ];
      formData.append('profile[portfolio]', JSON.stringify(portfolioData));
      formData.append(`portfolioImage_${newIndex}`, portfolio.imageFile);
      
      try {
        const response = await api.put(API_ENDPOINTS.USERS.UPDATE_PROFILE, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        updateUser(response.data);
        queryClient.invalidateQueries(['publicProfile', username]);
        toast.success('Portfolio item added successfully');
        setEditingPortfolio(null);
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to add portfolio item');
      }
    } else {
      // No image file, use existing method
      const updatedPortfolio = [...(user.profile?.portfolio || []), {
        title: portfolio.title,
        description: portfolio.description,
        url: portfolio.url,
        image: portfolio.image || ''
      }];
      handleSaveProfile('profile', { ...user.profile, portfolio: updatedPortfolio });
      setEditingPortfolio(null);
    }
  };

  const handleUpdatePortfolio = async (index, portfolio) => {
    // If portfolio has imageFile, we need to upload it
    if (portfolio.imageFile) {
      const formData = new FormData();
      const currentPortfolio = user.profile?.portfolio || [];
      const portfolioData = currentPortfolio.map((item, i) => {
        if (i === index) {
          return {
            title: portfolio.title,
            description: portfolio.description,
            url: portfolio.url,
            imageIndex: i
          };
        }
        return {
          title: item.title,
          description: item.description,
          url: item.url,
          image: item.image || item.imageUrl || ''
        };
      });
      formData.append('profile[portfolio]', JSON.stringify(portfolioData));
      formData.append(`portfolioImage_${index}`, portfolio.imageFile);
      
      try {
        const response = await api.put(API_ENDPOINTS.USERS.UPDATE_PROFILE, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        updateUser(response.data);
        queryClient.invalidateQueries(['publicProfile', username]);
        toast.success('Portfolio item updated successfully');
        setEditingPortfolio(null);
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to update portfolio item');
      }
    } else {
      // No image file, use existing method
      const updatedPortfolio = [...(user.profile?.portfolio || [])];
      updatedPortfolio[index] = {
        title: portfolio.title,
        description: portfolio.description,
        url: portfolio.url,
        image: portfolio.image || portfolio.imagePreview || updatedPortfolio[index]?.image || ''
      };
      handleSaveProfile('profile', { ...user.profile, portfolio: updatedPortfolio });
      setEditingPortfolio(null);
    }
  };

  const handleRemovePortfolio = (index) => {
    const updatedPortfolio = (user.profile?.portfolio || []).filter((_, i) => i !== index);
    handleSaveProfile('profile', { ...user.profile, portfolio: updatedPortfolio });
  };

  const stats = isOwnProfile
    ? privateStats?.stats || user?.stats || {}
    : user?.stats || {};

  const earnings = isOwnProfile
    ? privateStats?.earnings || { totalEarned: 0, totalSpent: 0, netEarnings: 0 }
    : { totalEarned: 0, totalSpent: 0, netEarnings: 0 };

  const activePublicRatingsData = viewingSomeoneElse ? publicRatingsData : ownerPublicRatingsData;

  const ratingSummarySource = isOwnProfile
    ? privateRatingsData?.rating || activePublicRatingsData?.rating || privateStats?.rating
    : publicRatingsData?.rating;

  const rating = ratingSummarySource || user?.stats?.rating || { average: 0, count: 0, totalScore: 0 };

  const ratings = activePublicRatingsData?.ratings ?? [];

  const ratingDistribution =
    activePublicRatingsData?.ratingDistribution ?? { ...EMPTY_RATING_DISTRIBUTION };

  let effectiveRatingDistribution = ratingDistribution;
  if (
    ratings.length > 0 &&
    Object.values(ratingDistribution).every((count) => count === 0 || count === undefined)
  ) {
    const fallbackDistribution = { ...EMPTY_RATING_DISTRIBUTION };
    ratings.forEach((entry) => {
      const rounded = Math.max(1, Math.min(5, Math.round(entry.rating)));
      fallbackDistribution[rounded] = (fallbackDistribution[rounded] || 0) + 1;
    });
    effectiveRatingDistribution = fallbackDistribution;
  }

  const ratingsLoading = viewingSomeoneElse
    ? publicRatingsLoading || !publicRatingsData
    : ownerPublicRatingsLoading || !ownerPublicRatingsData;

  const activity = isOwnProfile
    ? activityData || { transactions: [], recentJobs: [], recentGigs: [] }
    : { transactions: [], recentJobs: [], recentGigs: [] };

  const existingUserRating = currentUser
    ? ratings.find((entry) => {
        const reviewerId =
          typeof entry.fromUser === 'string'
            ? entry.fromUser
            : entry.fromUser?._id || entry.fromUser?.id;
        return reviewerId?.toString() === currentUser._id?.toString();
      })
    : null;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-heading mb-2">
              My Profile
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage your profile information and showcase your expertise
            </p>
          </div>
          {isOwnProfile && (
            <div className="flex items-center space-x-3">
              <button 
                className="btn-outline"
                onClick={() => window.open(`/profile/${user.username}`, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Public
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Profile Header Card */}
      <div className="card p-8 mb-8">
        <div className="flex items-start justify-between mb-6">
          {/* Profile Picture */}
          <div className="relative">
            <div className="w-24 h-24 bg-accent rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {user.profile?.avatar ? (
                <img 
                  src={user.profile.avatar} 
                  alt={user.username}
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                user.username[0].toUpperCase()
              )}
            </div>
            {isOwnProfile && (
              <button 
                className="absolute -bottom-2 -right-2 w-8 h-8 bg-white dark:bg-gray-800 border-2 border-accent rounded-full flex items-center justify-center text-accent hover:bg-accent hover:text-white transition-colors"
                onClick={() => setEditingField('avatar')}
              >
                <Camera className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Quick Stats */}
          <div className="flex items-center justify-between mb-4">
            <div className="grid grid-cols-3 gap-6 flex-1">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {rating.average.toFixed(1) || '0.0'}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <Star className="w-4 h-4 inline mr-1 fill-current text-yellow-500" />
                  Rating ({rating.count || 0})
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {stats.jobsCompleted || 0}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Jobs Completed
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {stats.activityPoints || 0}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Activity Points
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Profile Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {user.profile?.firstName || ''} {user.profile?.lastName || ''}
              {!user.profile?.firstName && !user.profile?.lastName && user.username}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-1">
              @{user.username}
            </p>
            {/* Social Links Icons */}
            {(user.profile?.socialLinks?.linkedin || 
              user.profile?.socialLinks?.github || 
              user.profile?.socialLinks?.twitter || 
              user.profile?.socialLinks?.website) && (
              <div className="flex items-center gap-3 mb-4">
                {user.profile?.socialLinks?.linkedin && (
                  <a
                    href={user.profile.socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-accent/80 transition-colors"
                    title="LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {user.profile?.socialLinks?.github && (
                  <a
                    href={user.profile.socialLinks.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-accent/80 transition-colors"
                    title="GitHub"
                  >
                    <Github className="w-5 h-5" />
                  </a>
                )}
                {user.profile?.socialLinks?.twitter && (
                  <a
                    href={user.profile.socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-accent/80 transition-colors"
                    title="X (Twitter)"
                  >
                    <X className="w-5 h-5" />
                  </a>
                )}
                {user.profile?.socialLinks?.website && (
                  <a
                    href={user.profile.socialLinks.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent hover:text-accent/80 transition-colors"
                    title="Website"
                  >
                    <Globe className="w-5 h-5" />
                  </a>
                )}
              </div>
            )}
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {user.profile?.bio || 'No bio added yet. Click edit to add your bio.'}
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
              {user.profile?.location && (
                <div className="flex items-center space-x-1">
                  <MapPin className="w-4 h-4" />
                  <span>{user.profile.location}</span>
                </div>
              )}
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>Joined {formatDate(user.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Edit Button */}
          <div className="flex items-start justify-end">
            {isOwnProfile && (
              <button
                onClick={() => setShowProfileEditModal(true)}
                className="btn-outline"
                title="Edit Profile"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
            activeTab === 'overview'
              ? 'bg-accent text-white'
              : 'text-gray-600 dark:text-gray-400 hover:text-accent'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('skills')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
            activeTab === 'skills'
              ? 'bg-accent text-white'
              : 'text-gray-600 dark:text-gray-400 hover:text-accent'
          }`}
        >
          Skills & Languages
        </button>
        <button
          onClick={() => setActiveTab('experience')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
            activeTab === 'experience'
              ? 'bg-accent text-white'
              : 'text-gray-600 dark:text-gray-400 hover:text-accent'
          }`}
        >
          Experience
        </button>
        <button
          onClick={() => setActiveTab('portfolio')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
            activeTab === 'portfolio'
              ? 'bg-accent text-white'
              : 'text-gray-600 dark:text-gray-400 hover:text-accent'
          }`}
        >
          Portfolio
        </button>
        <button
          onClick={() => setActiveTab('feedback')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
            activeTab === 'feedback'
              ? 'bg-accent text-white'
              : 'text-gray-600 dark:text-gray-400 hover:text-accent'
          }`}
        >
          Reviews & Ratings
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
            activeTab === 'activity'
              ? 'bg-accent text-white'
              : 'text-gray-600 dark:text-gray-400 hover:text-accent'
          }`}
        >
          Activity
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Basic Information
              </h3>
          {isOwnProfile && (
            <button 
              className="btn-outline"
              onClick={() => setEditingField('basic')}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
          )}
            </div>
            
            {isOwnProfile && editingField === 'basic' ? (
              <BasicInfoForm 
                user={user}
                onSave={(data) => {
                  handleSaveProfile('profile', { ...user.profile, ...data });
                  setEditingField(null);
                }}
                onCancel={() => setEditingField(null)}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                      First Name
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">
                      {user.profile?.firstName || 'Not set'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                      Email
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">
                      {user.email || 'Not set'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                      Wallet Address
                    </label>
                    <p className="text-gray-900 dark:text-gray-100 font-mono text-sm">
                      {user.walletAddress || 'Not connected'}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                      Last Name
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">
                      {user.profile?.lastName || 'Not set'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                      Phone
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">
                      {user.profile?.phone || 'Not set'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                      Role
                    </label>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 capitalize">
                      {user.role}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bio Section */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Bio
              </h3>
          {isOwnProfile && (
            <button 
              className="btn-outline"
              onClick={() => setEditingField('bio')}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
          )}
            </div>
        {isOwnProfile && editingField === 'bio' ? (
              <BioForm
                bio={user.profile?.bio || ''}
                onSave={(bio) => {
                  handleSaveProfile('profile', { ...user.profile, bio });
                  setEditingField(null);
                }}
                onCancel={() => setEditingField(null)}
              />
            ) : (
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {user.profile?.bio || 'No bio added yet. Click edit to add your bio.'}
              </p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'skills' && (
        <SkillsTab
          user={user}
          newSkill={newSkill}
          setNewSkill={setNewSkill}
          newLanguage={newLanguage}
          setNewLanguage={setNewLanguage}
          onAddSkill={handleAddSkill}
          onRemoveSkill={handleRemoveSkill}
          onAddLanguage={handleAddLanguage}
          onRemoveLanguage={handleRemoveLanguage}
          isOwnProfile={isOwnProfile}
        />
      )}

      {activeTab === 'experience' && (
        <ExperienceTab
          experience={user.profile?.experience || []}
          editingExperience={editingExperience}
          setEditingExperience={setEditingExperience}
          onAdd={handleAddExperience}
          onUpdate={handleUpdateExperience}
          onRemove={handleRemoveExperience}
          formatDate={formatDate}
          isOwnProfile={isOwnProfile}
        />
      )}

      {activeTab === 'portfolio' && (
        <PortfolioTab
          portfolio={user.profile?.portfolio || []}
          editingPortfolio={editingPortfolio}
          setEditingPortfolio={setEditingPortfolio}
          onAdd={handleAddPortfolio}
          onUpdate={handleUpdatePortfolio}
          onRemove={handleRemovePortfolio}
          isOwnProfile={isOwnProfile}
        />
      )}

      {activeTab === 'feedback' && (
        <FeedbackTab
          ratings={ratings}
          rating={rating}
        ratingDistribution={effectiveRatingDistribution}
          loading={ratingsLoading}
          canRate={!isOwnProfile && !!currentUser}
          submittingRating={submitRatingMutation.isLoading}
          existingUserRating={existingUserRating}
          onSubmitRating={handleSubmitRating}
          isAuthenticated={!!currentUser}
          showRatingActions={!isOwnProfile}
        />
      )}

      {activeTab === 'activity' && (
        <ActivityTab
          activity={activity}
          loading={activityLoading}
          userRole={user.role}
        />
      )}

      {/* Profile Edit Modal */}
      {showProfileEditModal && (
        <ProfileEditModal
          user={user}
          profileImagePreview={profileImagePreview}
          onClose={() => {
            setShowProfileEditModal(false);
            setProfileImageFile(null);
            setProfileImagePreview(null);
          }}
          onImageSelect={(file, preview) => {
            setProfileImageFile(file);
            setProfileImagePreview(preview);
          }}
          onSave={async (data) => {
            const formData = new FormData();
            
            // Add profile image if selected
            if (profileImageFile) {
              formData.append('avatar', profileImageFile);
            }
            
            // Add social links
            if (data.socialLinks) {
              formData.append('profile[socialLinks]', JSON.stringify(data.socialLinks));
            }
            
            try {
              const response = await api.put(API_ENDPOINTS.USERS.UPDATE_PROFILE, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              });
              updateUser(response.data);
              // Invalidate all user-related queries to refresh data
              queryClient.invalidateQueries(['publicProfile', username]);
              queryClient.invalidateQueries(['userStats', currentUser?._id]);
              queryClient.invalidateQueries(['auth', 'me']);
              toast.success('Profile updated successfully');
              setShowProfileEditModal(false);
              setProfileImageFile(null);
              setProfileImagePreview(null);
            } catch (error) {
              toast.error(error.response?.data?.error || 'Failed to update profile');
            }
          }}
        />
      )}
    </div>
  );
};

// Component for Basic Info Form
const BasicInfoForm = ({ user, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    firstName: user.profile?.firstName || '',
    lastName: user.profile?.lastName || '',
    phone: user.profile?.phone || '',
    location: user.profile?.location || '',
    email: user.email || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      location: formData.location,
    });
    if (formData.email && formData.email !== user.email) {
      // Email update will be handled separately
      api.put(API_ENDPOINTS.USERS.UPDATE_PROFILE, { email: formData.email })
        .then(() => toast.success('Email updated successfully'))
        .catch((err) => toast.error(err.response?.data?.error || 'Failed to update email'));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            First Name
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            className="input-field"
            placeholder="First Name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Last Name
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            className="input-field"
            placeholder="Last Name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="input-field"
            placeholder="Phone Number"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Location
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="input-field"
            placeholder="City, Country"
          />
        </div>
        {user.email && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input-field"
              placeholder="Email Address"
            />
          </div>
        )}
      </div>
      <div className="flex space-x-3">
        <button type="submit" className="btn-primary">
          <Save className="w-4 h-4 mr-2" />
          Save
        </button>
        <button type="button" onClick={onCancel} className="btn-outline">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Component for Bio Form
const BioForm = ({ bio, onSave, onCancel }) => {
  const [bioText, setBioText] = useState(bio);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(bioText);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <textarea
        value={bioText}
        onChange={(e) => setBioText(e.target.value)}
        className="input-field"
        rows={6}
        placeholder="Tell us about yourself..."
      />
      <div className="flex space-x-3">
        <button type="submit" className="btn-primary">
          <Save className="w-4 h-4 mr-2" />
          Save
        </button>
        <button type="button" onClick={onCancel} className="btn-outline">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Skills Tab Component
const SkillsTab = ({ user, newSkill, setNewSkill, newLanguage, setNewLanguage, onAddSkill, onRemoveSkill, onAddLanguage, onRemoveLanguage, isOwnProfile }) => {
  return (
    <div className="space-y-6">
      {/* Skills Section */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Skills & Expertise
        </h3>
        
        {isOwnProfile && (
          <div className="mb-4">
            <div className="flex items-center space-x-3 mb-2">
              <input
                type="text"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddSkill())}
                className="flex-1 input-field"
                placeholder="Enter a skill..."
              />
              <button onClick={onAddSkill} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {user.profile?.skills && user.profile.skills.length > 0 ? (
            user.profile.skills.map((skill, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-accent/20 text-accent rounded-full text-sm flex items-center space-x-2"
              >
                <span>{skill}</span>
                {isOwnProfile && (
                  <button
                    onClick={() => onRemoveSkill(skill)}
                    className="hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))
          ) : (
            <p className="text-gray-500 text-sm">No skills added yet</p>
          )}
        </div>
      </div>

      {/* Languages Section */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Languages
        </h3>
        
        {isOwnProfile && (
          <div className="mb-4">
            <div className="flex items-center space-x-3 mb-2">
              <input
                type="text"
                value={newLanguage}
                onChange={(e) => setNewLanguage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddLanguage())}
                className="flex-1 input-field"
                placeholder="Enter a language..."
              />
              <button onClick={onAddLanguage} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {user.profile?.languages && user.profile.languages.length > 0 ? (
            user.profile.languages.map((language, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm flex items-center space-x-2"
              >
                <span>{language}</span>
                {isOwnProfile && (
                  <button
                    onClick={() => onRemoveLanguage(language)}
                    className="hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))
          ) : (
            <p className="text-gray-500 text-sm">No languages added yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Experience Tab Component
const ExperienceTab = ({ experience, editingExperience, setEditingExperience, onAdd, onUpdate, onRemove, formatDate, isOwnProfile }) => {
  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Work Experience
        </h3>
        {isOwnProfile && (
          <button 
            className="btn-primary"
            onClick={() => setEditingExperience('new')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Experience
          </button>
        )}
      </div>

      {isOwnProfile && editingExperience === 'new' && (
        <ExperienceForm
          onSave={(exp) => {
            onAdd(exp);
            setEditingExperience(null);
          }}
          onCancel={() => setEditingExperience(null)}
        />
      )}

      <div className="space-y-4 mt-6">
        {experience.length > 0 ? (
          experience.map((exp, index) => (
            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              {isOwnProfile && editingExperience === index ? (
                <ExperienceForm
                  experience={exp}
                  onSave={(updatedExp) => {
                    onUpdate(index, updatedExp);
                    setEditingExperience(null);
                  }}
                  onCancel={() => setEditingExperience(null)}
                />
              ) : (
                <>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        {exp.position}
                      </h4>
                      <p className="text-accent">{exp.company}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(exp.startDate)} - {exp.current ? 'Present' : formatDate(exp.endDate)}
                      </p>
                      {exp.description && (
                        <p className="text-gray-700 dark:text-gray-300 mt-2">
                          {exp.description}
                        </p>
                      )}
                    </div>
                    {isOwnProfile && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingExperience(index)}
                          className="btn-outline text-xs"
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => onRemove(index)}
                          className="btn-outline text-xs text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))
        ) : (
          <p className="text-center py-8 text-gray-500 dark:text-gray-400">
            {isOwnProfile
              ? 'No work experience added yet. Click "Add Experience" to get started.'
              : 'No work experience shared yet.'}
          </p>
        )}
      </div>
    </div>
  );
};

// Experience Form Component
const ExperienceForm = ({ experience, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    company: experience?.company || '',
    position: experience?.position || '',
    startDate: experience?.startDate ? new Date(experience.startDate).toISOString().split('T')[0] : '',
    endDate: experience?.endDate ? new Date(experience.endDate).toISOString().split('T')[0] : '',
    description: experience?.description || '',
    current: experience?.current || false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      startDate: formData.startDate ? new Date(formData.startDate) : null,
      endDate: formData.current ? null : (formData.endDate ? new Date(formData.endDate) : null),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Company
          </label>
          <input
            type="text"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            className="input-field"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Position
          </label>
          <input
            type="text"
            value={formData.position}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            className="input-field"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Start Date
          </label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            className="input-field"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            End Date
          </label>
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            className="input-field"
            disabled={formData.current}
            required={!formData.current}
          />
          <label className="flex items-center space-x-2 mt-2">
            <input
              type="checkbox"
              checked={formData.current}
              onChange={(e) => setFormData({ ...formData, current: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">Currently working here</span>
          </label>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="input-field"
          rows={4}
        />
      </div>
      <div className="flex space-x-3">
        <button type="submit" className="btn-primary">
          <Save className="w-4 h-4 mr-2" />
          Save
        </button>
        <button type="button" onClick={onCancel} className="btn-outline">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Portfolio Tab Component
const PortfolioTab = ({ portfolio, editingPortfolio, setEditingPortfolio, onAdd, onUpdate, onRemove, isOwnProfile }) => {
  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Portfolio
        </h3>
        {isOwnProfile && (
          <button 
            className="btn-primary"
            onClick={() => setEditingPortfolio('new')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Project
          </button>
        )}
      </div>

      {isOwnProfile && editingPortfolio === 'new' && (
        <PortfolioForm
          onSave={(item) => {
            onAdd(item);
            setEditingPortfolio(null);
          }}
          onCancel={() => setEditingPortfolio(null)}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
        {portfolio.length > 0 ? (
          portfolio.map((item, index) => (
            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              {isOwnProfile && editingPortfolio === index ? (
                <PortfolioForm
                  portfolio={item}
                  onSave={(updatedItem) => {
                    onUpdate(index, updatedItem);
                    setEditingPortfolio(null);
                  }}
                  onCancel={() => setEditingPortfolio(null)}
                />
              ) : (
                <>
                  {item.image && (
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full h-48 object-cover rounded-lg mb-3"
                    />
                  )}
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {item.title}
                  </h4>
                  {item.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {item.description}
                    </p>
                  )}
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline text-sm"
                    >
                      View Project →
                    </a>
                  )}
                  {isOwnProfile && (
                    <div className="flex space-x-2 mt-3">
                      <button
                        onClick={() => setEditingPortfolio(index)}
                        className="btn-outline text-xs flex-1"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => onRemove(index)}
                        className="btn-outline text-xs text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
            {isOwnProfile
              ? 'No portfolio items added yet. Click "Add Project" to get started.'
              : 'No portfolio items shared yet.'}
          </div>
        )}
      </div>
    </div>
  );
};

// Profile Edit Modal Component
const ProfileEditModal = ({ user, profileImagePreview, onClose, onImageSelect, onSave }) => {
  const [formData, setFormData] = useState({
    socialLinks: {
      linkedin: user?.profile?.socialLinks?.linkedin || '',
      github: user?.profile?.socialLinks?.github || '',
      twitter: user?.profile?.socialLinks?.twitter || '',
      website: user?.profile?.socialLinks?.website || '',
    }
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onImageSelect(file, reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const socialMediaList = [
    { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/username' },
    { key: 'github', label: 'GitHub', icon: Github, placeholder: 'https://github.com/username' },
    { key: 'twitter', label: 'X (Twitter)', icon: X, placeholder: 'https://x.com/username' },
    { key: 'website', label: 'Website', icon: Globe, placeholder: 'https://yourwebsite.com' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Edit Profile
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Profile Image
              </label>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="w-24 h-24 bg-accent rounded-full flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                    {profileImagePreview ? (
                      <img 
                        src={profileImagePreview} 
                        alt="Profile preview"
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    ) : user?.profile?.avatar ? (
                      <img 
                        src={user.profile.avatar} 
                        alt={user.username}
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    ) : (
                      user?.username?.[0]?.toUpperCase()
                    )}
                  </div>
                  <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-white dark:bg-gray-800 border-2 border-accent rounded-full flex items-center justify-center text-accent hover:bg-accent hover:text-white transition-colors cursor-pointer">
                    <Camera className="w-4 h-4" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Click the camera icon to upload a new profile image
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended: Square image, at least 200x200px
                  </p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Social Links
              </label>
              <div className="space-y-3">
                {socialMediaList.map((social) => {
                  const Icon = social.icon;
                  return (
                    <div key={social.key} className="flex items-center space-x-3">
                      <Icon className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      <input
                        type="url"
                        value={formData.socialLinks[social.key]}
                        onChange={(e) => setFormData({
                          ...formData,
                          socialLinks: {
                            ...formData.socialLinks,
                            [social.key]: e.target.value
                          }
                        })}
                        className="input-field flex-1"
                        placeholder={social.placeholder}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button type="submit" className="btn-primary">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </button>
              <button type="button" onClick={onClose} className="btn-outline">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Portfolio Form Component
const PortfolioForm = ({ portfolio, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: portfolio?.title || '',
    description: portfolio?.description || '',
    url: portfolio?.url || '',
    image: portfolio?.image || portfolio?.imageUrl || '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(portfolio?.image || portfolio?.imageUrl || null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If image file is selected, we need to upload it first
    // For now, we'll pass the file and let the parent handle upload
    const portfolioData = {
      ...formData,
      imageFile: imageFile,
      imagePreview: imagePreview
    };
    
    onSave(portfolioData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Title
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="input-field"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="input-field"
          rows={3}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Project URL
        </label>
        <input
          type="url"
          value={formData.url}
          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
          className="input-field"
          placeholder="https://..."
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Project Image
        </label>
        {imagePreview && (
          <div className="mb-3">
            <img 
              src={imagePreview} 
              alt="Preview"
              className="w-full h-48 object-cover rounded-lg"
            />
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="input-field"
        />
        <p className="text-xs text-gray-500 mt-1">
          Upload an image from your device
        </p>
      </div>
      <div className="flex space-x-3">
        <button type="submit" className="btn-primary">
          <Save className="w-4 h-4 mr-2" />
          Save
        </button>
        <button type="button" onClick={onCancel} className="btn-outline">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Feedback Tab Component
const FeedbackTab = ({
  ratings,
  rating,
  ratingDistribution,
  loading,
  canRate,
  submittingRating,
  existingUserRating,
  onSubmitRating,
  isAuthenticated,
  showRatingActions
}) => {
  const [userRatingValue, setUserRatingValue] = useState(existingUserRating?.rating || 5);
  const [userComment, setUserComment] = useState(existingUserRating?.comment || '');

  useEffect(() => {
    setUserRatingValue(existingUserRating?.rating || 5);
    setUserComment(existingUserRating?.comment || '');
  }, [
    existingUserRating?._id,
    existingUserRating?.rating,
    existingUserRating?.comment
  ]);

  const handleRatingSubmit = (e) => {
    e.preventDefault();

    if (!canRate || !onSubmitRating) {
      return;
    }

    if (!userRatingValue) {
      toast.error('Please select a rating before submitting.');
      return;
    }

    onSubmitRating({
      rating: userRatingValue,
      comment: userComment
    });
  };

  const alreadyRated = Boolean(existingUserRating);

  if (loading) {
    return (
      <div className="card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  const totalRatings = Object.values(ratingDistribution).reduce((a, b) => a + b, 0);
  const fallbackTotal = rating.count || ratings.length;
  const effectiveTotalRatings = totalRatings > 0 ? totalRatings : fallbackTotal;

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Reviews & Ratings
        </h3>
        
        {/* Rating Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="text-center">
            <div className="text-5xl font-bold text-yellow-500 mb-2">
              {rating.average.toFixed(1) || '0.0'}
            </div>
            <div className="flex items-center justify-center mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star} 
                  className={`w-6 h-6 mr-1 ${
                    star <= Math.round(rating.average) 
                      ? 'text-yellow-500 fill-current' 
                      : 'text-gray-300 dark:text-gray-600'
                  }`} 
                />
              ))}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Based on {rating.count || 0} {rating.count === 1 ? 'review' : 'reviews'}
            </div>
          </div>
          
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = ratingDistribution[star] || 0;
              const percentage = effectiveTotalRatings > 0 ? (count / effectiveTotalRatings) * 100 : 0;
              return (
                <div key={star} className="flex items-center space-x-2">
                  <span className="text-sm font-medium w-8">{star}</span>
                  <Star className="w-3 h-3 text-yellow-500 fill-current" />
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-yellow-500 h-2 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500 w-8 text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

      {showRatingActions && (
        <div className="border-t border-gray-200 dark:border-gray-800 pt-6 mt-6">
          {canRate ? (
            <form onSubmit={handleRatingSubmit} className="space-y-4">
              <div>
                <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">
                  Share Your Experience
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Leave a rating and optional comment to help the community.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Overall Rating
                </label>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setUserRatingValue(star)}
                      className="focus:outline-none"
                      aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                    >
                      <Star
                        className={`w-6 h-6 transition-colors ${
                          star <= userRatingValue
                            ? 'text-yellow-500 fill-current'
                            : 'text-gray-300 dark:text-gray-600'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {userRatingValue} / 5
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Comment (optional)
                </label>
                <textarea
                  value={userComment}
                  onChange={(e) => setUserComment(e.target.value)}
                  className="input-field"
                  rows={4}
                  maxLength={2000}
                  placeholder="Tell others about your collaboration..."
                />
                <div className="text-xs text-gray-400 mt-1 text-right">
                  {userComment.length}/2000
                </div>
              </div>

              {alreadyRated && existingUserRating?.updatedAt && (
                <p className="text-xs text-gray-500">
                  Last updated {formatDistanceToNow(new Date(existingUserRating.updatedAt), { addSuffix: true })}.
                </p>
              )}

              <div className="flex items-center justify-end space-x-3">
                {alreadyRated && (
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => {
                      setUserRatingValue(existingUserRating?.rating || 5);
                      setUserComment(existingUserRating?.comment || '');
                    }}
                  >
                    Reset
                  </button>
                )}
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submittingRating}
                >
                  {submittingRating
                    ? 'Saving...'
                    : alreadyRated ? 'Update Review' : 'Submit Review'}
                </button>
              </div>
            </form>
          ) : (
            !isAuthenticated && (
              <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-4 text-center">
                <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Share Your Experience
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Sign in to leave a rating and comment for this professional.
                </p>
              </div>
            )
          )}
        </div>
      )}

        {/* Recent Reviews */}
        <div className="space-y-4">
          {ratings.length > 0 ? (
            ratings.map((ratingItem) => (
              <div key={ratingItem._id} className="border-l-4 border-accent pl-4 py-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {ratingItem.fromUser?.username?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {ratingItem.fromUser?.profile?.firstName || ratingItem.fromUser?.username || 'Anonymous'}
                      </span>
                      {ratingItem.job && (
                        <p className="text-xs text-gray-500">
                          {ratingItem.job.title}
                        </p>
                      )}
                      {ratingItem.gig && (
                        <p className="text-xs text-gray-500">
                          {ratingItem.gig.title}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`w-4 h-4 ${
                          star <= ratingItem.rating 
                            ? 'text-yellow-500 fill-current' 
                            : 'text-gray-300 dark:text-gray-600'
                        }`} 
                      />
                    ))}
                  </div>
                </div>
                {ratingItem.comment && (
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                    "{ratingItem.comment}"
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  {formatDistanceToNow(new Date(ratingItem.createdAt), { addSuffix: true })}
                </p>
              </div>
            ))
          ) : (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">
              No reviews yet
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Activity Tab Component
const ActivityTab = ({ activity, loading, userRole }) => {
  if (loading) {
    return (
      <div className="card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Recent Activity
        </h3>
        <div className="space-y-3">
          {activity.transactions && activity.transactions.length > 0 ? (
            activity.transactions.slice(0, 10).map((transaction) => (
              <div key={transaction._id} className="flex items-center space-x-3 py-2 border-b border-gray-100 dark:border-gray-800">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  transaction.type === 'payment' ? 'bg-green-100 dark:bg-green-900' :
                  transaction.type === 'withdrawal' ? 'bg-red-100 dark:bg-red-900' :
                  'bg-blue-100 dark:bg-blue-900'
                }`}>
                  {transaction.type === 'payment' ? (
                    <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                  ) : transaction.type === 'withdrawal' ? (
                    <DollarSign className="w-4 h-4 text-red-600 dark:text-red-400" />
                  ) : (
                    <Briefcase className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {transaction.description || `${transaction.type} - ${transaction.amount}`}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(transaction.createdAt), { addSuffix: true })}
                  </p>
                </div>
                <div className={`text-sm font-medium ${
                  transaction.direction === 'credit' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {transaction.direction === 'credit' ? '+' : '-'}${transaction.amount}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">
              No recent activity
            </p>
          )}
        </div>
      </div>

      {/* Recent Jobs/Gigs */}
      {(activity.recentJobs && activity.recentJobs.length > 0) || 
       (activity.recentGigs && activity.recentGigs.length > 0) ? (
        <>
          {activity.recentJobs && activity.recentJobs.length > 0 && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Recent Completed Jobs
              </h3>
              <div className="space-y-3">
                {activity.recentJobs.map((job) => (
                  <div key={job._id} className="flex items-center space-x-3 py-2">
                    <Briefcase className="w-5 h-5 text-accent" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {job.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        Completed {formatDistanceToNow(new Date(job.updatedAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activity.recentGigs && activity.recentGigs.length > 0 && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Recent Completed Gigs
              </h3>
              <div className="space-y-3">
                {activity.recentGigs.map((gig) => (
                  <div key={gig._id} className="flex items-center space-x-3 py-2">
                    <Briefcase className="w-5 h-5 text-accent" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {gig.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        Completed {formatDistanceToNow(new Date(gig.updatedAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default ProfilePage;
